import { Spacer } from "@hackthenorth/north";
import { Text } from "north.js";
import React, { useCallback, useMemo } from "react";
import styled from "styled-components";

import { errorToast, Modal } from "src/shared/components";
import { useMentorShiftsQuery } from "src/views/mentor/shifts/graphql/mentorShifts.generated";
import {
  formatVolunteerShiftDay,
  getVolunteerShiftHourAndMinute,
} from "src/views/volunteer/shared/utils/date";

type Props = {
  shiftId: number;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  spotsLeft: number;
  actionType: "register" | "unregister";
  onAction: (shiftId: number, index: number) => void;
  index: number;
};

const MentorRegisterModal = ({
  shiftId,
  open,
  setOpen,
  actionType,
  onAction,
  spotsLeft,
  index,
}: Props) => {
  const handleAction = async () => {
    try {
      onAction(shiftId, index); // Call the callback function
      setOpen(false);
    } catch (error) {
      errorToast(`Failed to ${actionType} for the shift. Please try again.`);
      console.error(`Error ${actionType} for shift:`, error);
    }
  };

  const {
    data: mentorShiftData,
    error,
    loading,
  } = useMentorShiftsQuery({
    variables: {
      where: {
        id: {
          equals: shiftId,
        },
      },
    },
  });

  const mentorShift = useMemo(() => {
    return mentorShiftData?.mentorShifts[0];
  }, [mentorShiftData]);

  const handleCloseModal = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  if (!mentorShiftData || error || loading || mentorShift === undefined) {
    return null;
  }
  return (
    <Modal
      height="auto"
      width="781px"
      scroll
      isOpen={open}
      onClose={handleCloseModal}
    >
      {/* TITLE */}
      <Text type="h3">{mentorShift.name}</Text>

      <Spacer height={24} />

      <InformationContainer>
        <InformationContainerRow>
          <div>
            <InformationText>Date</InformationText>
            <Spacer height={8} />
            <Text type="body1">
              {formatVolunteerShiftDay(new Date(mentorShift.start_time))}
            </Text>
          </div>

          <div>
            <InformationText>Time Start</InformationText>

            <Spacer height={8} />

            <Text type="body1">
              {getVolunteerShiftHourAndMinute(new Date(mentorShift.start_time))}
            </Text>
          </div>

          <div>
            <InformationText>Time End</InformationText>

            <Spacer height={8} />

            <Text type="body1">
              {getVolunteerShiftHourAndMinute(new Date(mentorShift.end_time))}
            </Text>
          </div>

          <div>
            <InformationText>Location</InformationText>

            <Spacer height={8} />

            <Text type="body1">{mentorShift.location}</Text>
          </div>
        </InformationContainerRow>

        <InformationContainerRow>
          <div>
            <InformationText>Spots Left</InformationText>

            <Spacer height={8} />

            <Text type="body1">{spotsLeft}</Text>
          </div>
        </InformationContainerRow>
      </InformationContainer>

      <Spacer height={16} />

      <FlexContainer>
        {actionType === "register" ? (
          <RegisterButton onClick={handleAction}>Register</RegisterButton>
        ) : (
          <UnregisterButton onClick={handleAction}>Unregister</UnregisterButton>
        )}
      </FlexContainer>
    </Modal>
  );
};

const InformationContainerRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 64px;
`;

const InformationContainer = styled.section`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const InformationText = styled.p`
  font-weight: bold;
  font-size: 20px;
  color: "#0E3460";
`;

const FlexContainer = styled.section`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
`;

const RegisterButton = styled.button`
  border-radius: 4px;
  border: 1px solid #2395c6;
  background: #43afde;
  box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.05);
  padding: 4px 10px;

  color: #fff;
  font-size: 16px;
  font-weight: 700;
  text-align: center;
`;

const UnregisterButton = styled.button`
  padding: 4px 10px;
  border-radius: 4px;
  border: 1px solid #d1d5db;
  background: #fff;
  box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.05);

  color: #d95858;
  font-size: 16px;
  font-weight: 700;
  text-align: center;
`;

export default MentorRegisterModal;
